import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getTenantSettings = createAsyncThunk(
  "TenantSettings/get",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.TENANT.SETTINGS);
      localStorage.setItem(
        "isTenantVerified",
        data?.data?.isOnboardingCompleted
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// NEW: Packing-specific tenant settings from correct API endpoint
// This is the source of truth for isServerSidePackingEnabled and isPicklistWorkflowEnabledAndMandatory
//
// IsPicklistWorkflowEnabledAndMandatory:
// false = Picklist workflow is COMPLETELY DISABLED (UI behaves as if feature doesn't exist)
// true  = Picklist workflow is MANDATORY (must create picklist before packing)
export const getTenantPackingSettings = createAsyncThunk(
  "TenantPackingSettings/get",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.TENANT.SETTINGS_V2);
      // API returns { isServerSidePackingEnabled: boolean, isPicklistWorkflowEnabledAndMandatory: boolean }
      return data?.data || data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCountry = createAsyncThunk(
  "Country/get",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.COUNTRY.SLUG);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveTenantSettings = createAsyncThunk(
  "TenantSettings/save",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.TENANT.SETTINGS, payload);
      await dispatch(getTenantSettings());
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateTenantSettings = createAsyncThunk(
  "TenantSettings/update",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(API_URL.TENANT.SETTINGS, payload);
      dispatch(getTenantSettings());
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getTenantDocuments = createAsyncThunk(
  "TenantDocuments/get",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.TENANT.SETTINGS}/TenantDocument`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadTenantDocument = createAsyncThunk(
  "profile/uploadTenantDocument",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", payload.file);

      const response = await axios.post(
        `${API_URL.TENANT.SETTINGS}/UploadDocument`,
        formData,
        {
          params: {
            tenantDocumentType: payload.tenantDocumentType, // NUMBER
            tenantSettingsId: payload.tenantSettingsId, // GUID
          },
        }
      );
      dispatch(getTenantDocuments());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const replaceTenantDocument = createAsyncThunk(
  "profile/replaceTenantDocument",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("File", payload.file);

      const response = await axios.post(
        `${API_URL.TENANT.SETTINGS}/ReplaceDocument`,
        formData,
        {
          params: {
            tenantDocumentId: payload.tenantDocumentId,
            tenantSettingsId: payload.tenantSettingsId,
          },
        }
      );
      dispatch(getTenantDocuments());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const deleteTenantDocument = createAsyncThunk(
  "profile/deleteTenantDocument",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL.TENANT.SETTINGS}/DeleteDocument/${payload.tenantDocumentId}`
      );
      dispatch(getTenantDocuments());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    data: null,
    loading: false,
    error: null,
    userValues: null,
    tenant_setting: {},
    tenantPackingSettings: null, // NEW: Packing-specific settings (null = loading)
    country: [],
    tenant_documents: [],
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTenantSettings.fulfilled, (state, action) => {
        state.tenant_setting = action.payload;
      })
      .addCase(getTenantPackingSettings.fulfilled, (state, action) => {
        state.tenantPackingSettings = action.payload;
      })
      .addCase(getCountry.fulfilled, (state, action) => {
        state.country = action.payload;
      })
      .addCase(getTenantDocuments.fulfilled, (state, action) => {
        state.tenant_documents = action.payload;
      });
  },
});

export default profileSlice.reducer;
